import React from 'react';
import ServiceTemplate from '@/components/ServiceTemplate';

const GST = () => {
  const data = {
    title: 'GST Registration & Filing Services',
    description: 'Get your GST registration done quickly and start your business operations legally. Expert assistance for GST compliance, returns filing, and consultancy.',
    keywords: 'gst registration, goods and services tax, gstin, gst filing, tax registration india, gst certificate, gst number',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          GST (Goods and Services Tax) is an indirect tax levied on the supply of goods and services in India. 
          Introduced on July 1, 2017, GST has replaced multiple indirect taxes like VAT, Service Tax, Excise Duty, 
          and others. It's a destination-based tax system that has simplified the tax structure and made compliance easier.
        </p>
        <p>
          <strong>Who Needs GST Registration?</strong> If your annual turnover exceeds ₹40 lakhs for goods (₹20 lakhs 
          for special category states) or ₹20 lakhs for services, GST registration is mandatory. Additionally, 
          businesses involved in inter-state supply, e-commerce sellers, casual taxable persons, and non-resident 
          taxable persons must register regardless of turnover.
        </p>
        <p>
          <strong>Types of GST:</strong> There are four types of GST in India - CGST (Central GST), SGST (State GST), 
          IGST (Integrated GST for inter-state transactions), and UTGST (Union Territory GST). Businesses collect 
          GST from customers and pay it to the government after claiming input tax credit on their purchases.
        </p>
        <p>
          <strong>Benefits of GST Registration:</strong> GST registration legitimizes your business, allows you to 
          collect GST from customers, claim input tax credit, compete for government tenders, and expand business 
          across India without multiple state registrations. It's essential for scaling your business and maintaining 
          legal compliance.
        </p>
      </div>
    ),
    
    benefits: [
      'Legal recognition and legitimacy for your business operations',
      'Ability to collect GST from customers and pass on tax liability',
      'Input tax credit on purchases, reducing overall tax burden',
      'Mandatory for businesses with turnover above ₹40 lakhs (goods) or ₹20 lakhs (services)',
      'Better credibility and trust with clients and suppliers',
      'Required for inter-state transactions and business expansion',
      'Access to government tenders and contracts',
      'Seamless credit flow across the supply chain',
      'Composition scheme available for small businesses (up to ₹1.5 crore turnover)',
      'Easier compliance compared to previous tax regime',
      'E-commerce marketplace selling becomes possible',
      'Competitive advantage in B2B transactions',
    ],
    
    documents: [
      'PAN Card of business entity/proprietor (mandatory for GST)',
      'Aadhaar Card of proprietor/all directors/partners',
      'Recent photograph of proprietor/directors/partners (passport size)',
      'Address proof of principal place of business (sale deed, rent agreement, or electricity bill)',
      'Bank account statement or cancelled cheque with business name',
      'Rent agreement or NOC from property owner (notarized)',
      'Board resolution or authorization letter (for companies)',
      'Certificate of Incorporation or Partnership Deed (as applicable)',
      'Identity and address proof of authorized signatory',
      'Details of all additional places of business (if any)',
      'Digital Signature (for companies)',
    ],
    
    process: [
      'Document collection and verification - We review all documents for accuracy',
      'Business classification and HSN/SAC code identification',
      'Application filing on GST portal (Form GST REG-01)',
      'ARN (Application Reference Number) generation - Track application status',
      'Verification by GST officer - May include site visit or document verification',
      'GSTIN (GST Identification Number) issued - 15-digit unique number',
      'GST registration certificate download from portal',
      'Bank account linking for GST refunds and credits',
      'Setup of GST portal access and digital signature',
      'Training on GST compliance and return filing basics',
    ],
    
    timeline: '5-7 Working Days',
    pricing: '₹2,999 (All Inclusive)',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>GST Return Filing Frequency:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>GSTR-1: Monthly/Quarterly (details of outward supplies)</li>
          <li>GSTR-3B: Monthly (summary return with tax payment)</li>
          <li>GSTR-9: Annual return (to be filed by December 31)</li>
        </ul>
        <p className="mt-3"><strong>GST Rates in India:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>0% - Essential goods like milk, eggs, fresh vegetables</li>
          <li>5% - Household necessities, economy class tickets</li>
          <li>12% - Processed food, computers, business class tickets</li>
          <li>18% - Most goods and services, capital goods</li>
          <li>28% - Luxury items, tobacco, automobiles</li>
        </ul>
        <p className="mt-3"><strong>Composition Scheme:</strong> Small businesses with turnover up to ₹1.5 crore can opt for 
        the composition scheme with reduced tax rates (1-6%) and quarterly filing. However, they cannot claim input tax credit 
        or make inter-state supplies.</p>
        <p className="mt-3"><strong>Our Service Includes:</strong> GST registration, GSTIN certificate, portal setup, 
        GST compliance guidance, and 1 month of free consultation for return filing queries.</p>
      </div>
    ),
    
    faqs: [
      {
        question: 'Who needs GST registration?',
        answer: 'Businesses with annual turnover exceeding ₹40 lakhs for goods (₹20 lakhs for North-Eastern and hilly states) or ₹20 lakhs for services must register for GST. E-commerce sellers, inter-state suppliers, agents, and input service distributors need GST regardless of turnover. Voluntary registration is also allowed for businesses below the threshold.',
      },
      {
        question: 'How long is GST registration valid?',
        answer: 'GST registration does not have an expiry date. It remains valid until cancelled, surrendered, or suspended by the taxpayer or authorities. However, you must file regular returns and maintain compliance to keep the registration active.',
      },
      {
        question: 'Can I get GST registration for multiple states?',
        answer: 'Yes, you need separate GST registration for each state where you have a business presence or place of business. Each state registration will have a unique 15-digit GSTIN starting with the respective state code.',
      },
      {
        question: 'What is the difference between regular GST and composition scheme?',
        answer: 'Regular GST requires detailed monthly return filing with input tax credit eligibility and inter-state supply permission. Composition scheme is for small businesses (turnover up to ₹1.5 cr) with simplified quarterly filing, reduced tax rates (1-6%), but no input tax credit and no inter-state sales allowed.',
      },
      {
        question: 'What are the penalties for late GST registration?',
        answer: 'Late GST registration can attract penalties of ₹10,000 (₹5,000 CGST + ₹5,000 SGST) or 10% of tax due, whichever is higher. Additionally, there are late fees of ₹50 per day (₹20 for nil return) for delayed return filing, capped at ₹5,000.',
      },
      {
        question: 'Can I cancel my GST registration?',
        answer: 'Yes, GST registration can be cancelled if business is closed, turnover falls below threshold, business structure changes, or on transfer/amalgamation. Submit Form GST REG-16 for cancellation. Suo moto cancellation by authorities can also happen for non-filing of returns for 6 months.',
      },
      {
        question: 'What is input tax credit (ITC)?',
        answer: 'Input Tax Credit allows businesses to claim credit for GST paid on purchases/inputs and use it to offset GST liability on sales/outputs. This eliminates the cascading effect of taxes. ITC is available only to regular GST registrants, not to composition scheme taxpayers.',
      },
      {
        question: 'Is GST registration free?',
        answer: 'GST registration with the government is free. However, you can take professional assistance for documentation, application filing, and compliance guidance. Our fees of ₹2,999 include complete registration process, documentation, filing, and compliance guidance.',
      },
    ],
  };

  return <ServiceTemplate {...data} />;
};

export default GST;